import { Button, CircularProgress, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Typography, Grid } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ClearIcon from '@material-ui/icons/Clear';
import TableChartOutlinedIcon from '@material-ui/icons/TableChartOutlined';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { composeWhereClause, displayChildAge, displayFacilityType } from '../../modules/data';
import { rdsSelect, rdsTabulate } from '../../modules/rds';
import states, { stateFIPS } from '../../modules/states';
import { toFormattedNumber } from '../../modules/text';

function CountyComparisonTable() {
  const { stateCode } = useParams();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const { settingType, childAge } = useContext(FiltersContext);
  const { primaryColor } = useContext(StoryContext);
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCounties, setSelectedCounties] = useState([]);

  useEffect(() => {
    function getChildrenCount() {
      const dataProduct = 'acs_sams_county_wide';
      const where = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND (county_suppressed=0 OR county_suppressed=NULL)`;

      return rdsSelect('3rdparty', dataProduct, {
        cols: 'county_fips,cnt_parents_inlf_total',
        where,
        format: 'amcharts',
        inject: true,
      });
    }

    function getStateChildrenCount() {
      const dataProduct = 'acs_sams_state_wide';
      const where = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
      return rdsSelect('3rdparty', dataProduct, {
        cols: 'cnt_parents_inlf_total',
        where,
        format: 'amcharts',
        inject: false,
      });
    }

    function getCapacity() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]} AND capacity_type=LIC`;
      whereClause += composeWhereClause({ settingType, childAge });

      return rdsTabulate('ccdc', 'county_licensed_capacity', {
        dims: 'county_fips',
        measure: 'capacity:SUM(capacity)',
        where: whereClause,
        format: 'amcharts',
        inject: true,
      }, true);
    }

    Promise.all([getCapacity(), getChildrenCount(), getStateChildrenCount()])
      .then((results) => {
        const d = results[0].parsedBody.dataProvider;
        const countyData = results[1].parsedBody.dataProvider;

        const totalCapacity = d.reduce((a, v) => (a + Number.parseInt(v.capacity, 10)), 0);
        const totalChildren = results[2].parsedBody.dataProvider[0].cnt_parents_inlf_total;
        setStateData({ totalCapacity, totalChildren });
        const payload = d.map((v) => {
          const result = Object.assign(v, {
            totalCapacity,
            totalChildren,
          });

          const countyChildren = _.find(countyData, { county_fips: v.county_fips });
          if (countyChildren) {
            result.children = countyChildren.cnt_parents_inlf_total;
          }
          if (result.children) {
            result.value = ((result.capacity / result.children) * 100);
          }
          return result;
        });
        // realign data. Update selected counties with fresh data
        const fips = selectedCounties.map((c) => (c.county_fips));
        if (fips.length > 0) {
          setSelectedCounties(_.filter(payload, (o) => fips.includes(o.county_fips)));
        }

        // set data
        setData(payload);
      })
      .catch((rdsError) => {
        setError(rdsError);
      })
      .finally(() => {
        setReady(true);
      });
  }, [stateCode, settingType, childAge]);

  const handleSelectionChange = (event) => {
    setSelectedCounty(event.target.value);
  };

  const handleAddCounty = () => {
    if (!_.includes(selectedCounties, selectedCounty)) {
      setSelectedCounties(selectedCounties.concat([selectedCounty]));
    }
    setSelectedCounty('');
  };

  const removeCounty = (c) => (e) => {
    if (!c) return;
    setSelectedCounties(selectedCounties.filter((f) => (f.county_fips !== c.county_fips)));
  };

  if (!ready) {
    return <CircularProgress />;
  }

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        color: primaryColor,
      }}
      >
        <TableChartOutlinedIcon />&nbsp;<Typography variant="h5">County Comparison Table</Typography>
      </div>
      <Grid container>
        <Grid item xs={8} sm={6} lg={3}>
          <FormControl>
            <InputLabel id="countySelect">County</InputLabel>
            <Select
              labelId="countySelect"
              id="countySelectHelper"
              value={selectedCounty}
              onChange={handleSelectionChange}
            >
              {data.map((d) => (<MenuItem value={d} key={d.county_fips}>{d.county_fips}</MenuItem>))}

            </Select>
            <FormHelperText>Select a county from the menu to add to the comparison table</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={4} sm={6} lg={9}>
          <Button
            disabled={!selectedCounty}
            variant="contained"
            onClick={handleAddCounty}
            style={{
              marginLeft: '20px', marginBottom: '-70px', backgroundColor: primaryColor, color: '#fff',
            }}
            size="small"
          >Add to table
          </Button>
        </Grid>
      </Grid>
      <Table size="small" aria-label="County comparison table" style={{ overflowX: 'auto', display: 'block' }}>
        <TableHead>
          <TableRow>
            <TableCell>County</TableCell>
            <TableCell align="right"><Typography variant="body2">{displayFacilityType(settingType)} slots</Typography></TableCell>
            <TableCell align="right">
              <Typography variant="body2">
                Slots per 100 {displayChildAge(childAge, true).toLowerCase()}
              </Typography>
            </TableCell>

            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow selected>
            <TableCell component="th" scope="row">
              {states[stateCode.toUpperCase()]} (State)
            </TableCell>
            <TableCell align="right"><Typography variant="body2">{toFormattedNumber(stateData.totalCapacity)}</Typography></TableCell>
            <TableCell align="right"><Typography variant="body2">{toFormattedNumber((stateData.totalCapacity / (stateData.totalChildren)) * 100)}</Typography></TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
          {selectedCounties.map((row) => (
            <TableRow key={row.county_fips}>
              <TableCell component="th" scope="row">
                <Typography variant="body2">{row.county_fips}</Typography>
              </TableCell>
              <TableCell align="right"><Typography variant="body2">{row.capacity ? toFormattedNumber(row.capacity) : 'No data'}</Typography></TableCell>
              <TableCell align="right"><Typography variant="body2">{row.children ? toFormattedNumber((row.capacity / row.children) * 100) : 'No data'}</Typography></TableCell>
              <TableCell><IconButton size="small" onClick={removeCounty(row)} aria-label={`Remove ${row.county}`}><ClearIcon /></IconButton></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

export default CountyComparisonTable;
